// Parses an editor-supplied video URL into something the renderer can embed.
// Supported: YouTube (watch / youtu.be / shorts / embed), Vimeo, and direct
// .mp4/.webm/.mov files. Anything else → 'unknown' and the block hides.

export type ParsedVideo =
  | { kind: 'youtube'; embedUrl: string }
  | { kind: 'vimeo'; embedUrl: string }
  | { kind: 'file'; src: string }
  | { kind: 'unknown' };

export function parseVideoUrl(rawUrl: string): ParsedVideo {
  const url = (rawUrl || '').trim();
  if (!url) return { kind: 'unknown' };

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return { kind: 'unknown' };
  }
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
    return { kind: 'unknown' };
  }

  const host = parsed.hostname.replace(/^www\./, '').toLowerCase();

  // YouTube variants → privacy-enhanced embed host
  if (host === 'youtu.be') {
    const id = parsed.pathname.split('/').filter(Boolean)[0];
    if (id) return { kind: 'youtube', embedUrl: `https://www.youtube-nocookie.com/embed/${id}` };
  }
  if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'youtube-nocookie.com') {
    const watchId = parsed.searchParams.get('v');
    if (watchId) {
      return { kind: 'youtube', embedUrl: `https://www.youtube-nocookie.com/embed/${watchId}` };
    }
    const parts = parsed.pathname.split('/').filter(Boolean);
    if ((parts[0] === 'shorts' || parts[0] === 'embed' || parts[0] === 'live') && parts[1]) {
      return { kind: 'youtube', embedUrl: `https://www.youtube-nocookie.com/embed/${parts[1]}` };
    }
  }

  // Vimeo: vimeo.com/<id> (optionally with an unlisted hash segment)
  if (host === 'vimeo.com' || host === 'player.vimeo.com') {
    const parts = parsed.pathname.split('/').filter(Boolean);
    const idIndex = parts[0] === 'video' ? 1 : 0;
    const id = parts[idIndex];
    if (id && /^\d+$/.test(id)) {
      const hash = parts[idIndex + 1];
      const suffix = hash && /^[a-zA-Z0-9]+$/.test(hash) ? `?h=${hash}` : '';
      return { kind: 'vimeo', embedUrl: `https://player.vimeo.com/video/${id}${suffix}` };
    }
  }

  // Direct video files (e.g. Supabase Storage public URLs)
  if (/\.(mp4|webm|mov)$/i.test(parsed.pathname)) {
    return { kind: 'file', src: url };
  }

  return { kind: 'unknown' };
}
